'use client';

import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { SparseAsset } from '@/asset/api/types';
import { PersonSearch } from '@/components/person-search';
import { SparseProject } from '@/sparse/api/types';
import { createTicket } from '@/ticket/api/create-ticket';
import {
  CreateTicketData,
  TICKET_DESCRIPTION_MAX_LENGTH,
  TICKET_DESCRIPTION_MAX_LENGTH_MESSAGE,
  TicketCategory,
} from '@/ticket/api/types';
import { TICKET_CATEGORY_DISPLAY_MAP } from '@/ticket/util/labels';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface CreateTicketProps {
  initialCategory?: TicketCategory;
  onClose: () => void;
  forAssetIds?: SparseAsset[];
  forProjectIds?: SparseProject[];
  onSuccess?: () => void;
  additionalQueryKeys?: string[][];
}

export function CreateTicket(props: CreateTicketProps) {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Form setup with React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateTicketData>({
    defaultValues: {
      title: '',
      description: '',
      category: props.initialCategory || 'MISC',
      assignedTo: null,
      assets: props.forAssetIds ?? [],
      projects: props.forProjectIds ?? [],
    },
  });

  // Mutation for creating a ticket
  const createTicketMutation = useMutation({
    mutationFn: createTicket,
    onSuccess: () => {
      setSuccessMessage('Ticket created successfully!');
      reset(); // Reset form after successful submission
      props.onClose();

      // Invalidate the general tickets query
      queryClient.invalidateQueries({ queryKey: ['tickets'] });

      // Invalidate any additional query keys provided
      if (props.additionalQueryKeys) {
        props.additionalQueryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Call the success callback if provided
      if (props.onSuccess) {
        props.onSuccess();
      }
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      setErrorMessage('Failed to create ticket. Please try again.');
      console.error('Error creating ticket:', error);
    },
  });

  // Form submission handler
  const onSubmit = (data: CreateTicketData) => {
    createTicketMutation.mutate({
      data,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Title Field */}
          <Controller
            name="title"
            control={control}
            rules={{
              required: 'Title is required',
              maxLength: {
                value: 160,
                message: 'Title cannot exceed 160 characters',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Title"
                placeholder="Brief summary of the issue"
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            )}
          />
          {/* Description Field */}
          <Controller
            name="description"
            control={control}
            rules={{
              required: 'Description is required',
              maxLength: {
                value: TICKET_DESCRIPTION_MAX_LENGTH,
                message: TICKET_DESCRIPTION_MAX_LENGTH_MESSAGE,
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={4}
                placeholder="Detailed description of the issue"
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
          {/* Category Field */}
          <Controller
            name="category"
            control={control}
            rules={{ required: 'Category is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  {...field}
                  labelId="category-label"
                  label="Category"
                  size={'small'}
                >
                  {Object.entries(TICKET_CATEGORY_DISPLAY_MAP).map(
                    ([category, displayName]) => (
                      <MenuItem key={category} value={category}>
                        {displayName}
                      </MenuItem>
                    )
                  )}
                </Select>
                {errors.category && (
                  <FormHelperText>{errors.category.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          {/* Assigned To Field */}
          <Controller
            name="assignedTo"
            control={control}
            render={({ field }) => (
              <PersonSearch
                label={'Assignee'}
                value={field.value}
                onChange={(user) => {
                  field.onChange(user);
                }}
              />
            )}
          />
        </Stack>

        {/* Success/Error Messages */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
        >
          <Alert severity="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
        >
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color="primary" size="small">
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Open
        </Button>
      </DialogActions>
    </form>
  );
}
